import { Zoning, ZoningType } from '@popety-io/types'
import { ZONING_TYPE_PRIORITY, METRIC_DEFINITIONS } from './zoningConstants'

/**
 * Organizes zoning data by type for easy access
 */
export interface ZoningDataByType {
  [key: string]: Zoning[]
}

/**
 * Converts a number to Roman numerals (1-10)
 */
const toRomanNumeral = (num: number): string => {
  const romanNumerals: Record<number, string> = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V',
    6: 'VI',
    7: 'VII',
    8: 'VIII',
    9: 'IX',
    10: 'X',
  }
  const rounded = Math.round(num)
  return romanNumerals[rounded] || rounded.toString()
}

/**
 * Formats a metric value with its unit
 * For surface metrics (m², m³), uses Swiss format with apostrophe separators and no decimals
 * For count metrics (floors, units), uses integer format
 * For percentage ratios (%), multiplies by 100 and shows as integer percentage
 * For noise sensitivity level, uses Roman numerals
 * For other metrics, uses 2 decimal places
 */
export const formatMetricValue = (
  value: number | string | undefined,
  unit?: string,
  metricKey?: string,
  status?: string
): string => {
  if (status === 'not_applicable' && !value) {
    return 'Not applicable'
  }

  if (value === undefined || value === null) {
    return '-'
  }

  // Convert to number if string (API might return string values)
  const numericValue = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(numericValue)) {
    return '-'
  }

  let formattedValue: string

  // Noise sensitivity level uses Roman numerals
  if (metricKey === 'noise_sensibility_level') {
    return toRomanNumeral(numericValue)
  }

  // Percentage ratios: API returns 0.3 for 30%, convert and show as integer
  if (unit === '%') {
    const percentage = Math.round(numericValue * 100)
    formattedValue = percentage.toString()
  } else if (unit === 'm²' || unit === 'm³') {
    // Surface metrics (m², m³) use Swiss format with apostrophes, no decimals
    const rounded = Math.round(numericValue)
    formattedValue = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")
  } else if (
    metricKey &&
    (METRIC_DEFINITIONS[metricKey.replace(/_min$|_max$/, '')]?.category ===
      'floor' ||
      metricKey.replace(/_min$|_max$/, '') === 'unit_nb')
  ) {
    // Count metrics (floors, units) show as integers
    formattedValue = Math.round(numericValue).toString()
  } else {
    // Other metrics keep 2 decimal places
    formattedValue = numericValue.toFixed(2)
  }

  return unit ? `${formattedValue} ${unit}` : formattedValue
}

/**
 * Gets the display label for a metric
 */
export const getMetricLabel = (metricKey: string): string => {
  // Remove _min or _max suffix to get base metric key
  const baseKey = metricKey.replace(/_min$|_max$/, '')
  const definition = METRIC_DEFINITIONS[baseKey]
  return definition?.label || metricKey
}

/**
 * Gets the unit for a metric
 */
export const getMetricUnit = (metricKey: string): string | undefined => {
  const baseKey = metricKey.replace(/_min$|_max$/, '')
  const definition = METRIC_DEFINITIONS[baseKey]
  return definition?.unit
}

/**
 * Checks if a metric value should be displayed as percentage
 * (stored as decimal 0.3, displayed as 30%)
 */
export const isPercentageMetric = (metricKey: string): boolean => {
  const baseKey = metricKey.replace(/_min$|_max$/, '')
  const definition = METRIC_DEFINITIONS[baseKey]
  return definition?.isPercentage === true
}

/**
 * Converts a metric value for display (handles percentage conversion)
 */
export const convertValueForDisplay = (
  value: number | undefined,
  metricKey: string
): number | undefined => {
  if (value === undefined) return undefined
  if (isPercentageMetric(metricKey)) {
    return Math.round(value * 100)
  }
  return value
}

/**
 * Determines which zoning types have data
 */
export const getAvailableZoningTypes = (
  data: ZoningDataByType
): ZoningType[] => {
  return ZONING_TYPE_PRIORITY.filter((type) => {
    const zones = data[type as keyof ZoningDataByType]
    return zones && zones.length > 0
  })
}

/**
 * Gets the zoning type to select by default.
 *
 * Prefers the communal (general) plan when present — it is the base zoning
 * that defines the fundamental land use — and otherwise falls back to the
 * highest-priority available type (cantonal → local → partial).
 */
export const getDefaultZoningType = (
  data: ZoningDataByType
): ZoningType | null => {
  const available = getAvailableZoningTypes(data)
  if (available.length === 0) return null
  return available.includes('general') ? 'general' : available[0]
}

/**
 * Counts zones by type
 */
export const getZoneCounts = (data: ZoningDataByType) => {
  return {
    general: data.general?.length || 0,
    cantonal: data.cantonal?.length || 0,
    local: data.local?.length || 0,
    partial: data.partial?.length || 0,
  }
}

/**
 * Extracts all populated metrics from a zone
 * Returns array of [metricKey, minValue, maxValue, source]
 */
export const getPopulatedMetrics = (zone: Zoning) => {
  const metrics: Array<{
    key: string
    min: number | undefined
    max: number | undefined
    minSource?: string
    maxSource?: string
    minReference?: any
    maxReference?: any
  }> = []

  // List of all metrics to check
  const metricKeys = Object.keys(METRIC_DEFINITIONS)
  const zoneData = zone as any

  metricKeys.forEach((key) => {
    // Support both v1 (_min/_max) and v2.0 (_min_value/_max_value, _value) field patterns
    const minValue =
      (zoneData[`${key}_min`] as number | undefined) ??
      (zoneData[`${key}_min_value`] as number | undefined)
    const maxValue =
      (zoneData[`${key}_max`] as number | undefined) ??
      (zoneData[`${key}_max_value`] as number | undefined) ??
      (zoneData[`${key}_value`] as number | undefined)

    const minSource =
      (zoneData[`${key}_min_source`] as string | undefined) ??
      (zoneData[`${key}_source`] as string | undefined)
    const maxSource =
      (zoneData[`${key}_max_source`] as string | undefined) ??
      (zoneData[`${key}_source`] as string | undefined)

    const minRefKey = `${key}_min_reference` as keyof Zoning
    const maxRefKey = `${key}_max_reference` as keyof Zoning

    // Include if at least one value exists (including null = researched but N/A)
    // Also include if v2 source data exists (metric was researched even if value is null)
    const hasV2Sources =
      zoneData[`${key}_max_default_source`] !== undefined ||
      zoneData[`${key}_min_default_source`] !== undefined
    if (minValue != null || maxValue != null || hasV2Sources) {
      metrics.push({
        key,
        min: minValue ?? undefined,
        max: maxValue ?? undefined,
        minSource,
        maxSource,
        minReference: zone[minRefKey],
        maxReference: zone[maxRefKey],
      })
    }
  })

  return metrics
}

/**
 * Gets the primary identifier for a zone (for display)
 */
export const getZonePrimaryId = (zone: Zoning, type: ZoningType): string => {
  switch (type) {
    case 'cantonal':
      return zone.lupc_id || zone.lupc_name || 'Unknown'
    case 'local':
      return zone.lupo_id || zone.lupo_name || 'Unknown'
    case 'partial':
      return zone.lupo_id || zone.lupo_name || 'Unknown'
    case 'general':
    default:
      return zone.lup_id || zone.lup_name || 'Unknown'
  }
}

/**
 * Gets the primary name for a zone
 */
export const getZonePrimaryName = (zone: Zoning, type: ZoningType): string => {
  switch (type) {
    case 'cantonal':
      return zone.lupc_name || zone.lupac_name || 'Unknown Zone'
    case 'local':
      return zone.lupo_name || zone.lupao_name || 'Unknown Zone'
    case 'partial':
      return zone.lupo_name || zone.lupao_name || 'Unknown Zone'
    case 'general':
    default:
      return zone.lup_name || zone.lupa_name || 'Unknown Zone'
  }
}

/**
 * Gets the plan type for display
 */
export const getZonePlanType = (
  zone: Zoning,
  type: ZoningType
): string | undefined => {
  switch (type) {
    case 'cantonal':
      return zone.lupc_plan_type
    case 'local':
    case 'partial':
      return zone.lupo_plan_type
    case 'general':
    default:
      return zone.lup_plan_type
  }
}

/**
 * Formats a zone status for display
 */
export const formatZoneStatus = (status?: string): string => {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/**
 * Returns exclusive metric groups
 * Metrics in the same group are mutually exclusive
 */
export const getExclusiveGroups = () => {
  return {
    group1: ['ios', 'cos'], // Occupation metrics
    group2: ['ius', 'cus', 'ibus', 'im'], // Utilization metrics
  }
}

/**
 * Get smart default metric selection based on actual data availability
 * Selects the first metric with data (min or max values) following priority order
 * Priority: IOS → COS (group1), IBUS → IUS → CUS → IM (group2)
 * Returns 'none' if no metrics have data
 */
export const getSmartDefaultMetricSelection = (
  zone: Zoning
): {
  group1: 'ios' | 'cos' | 'none'
  group2: 'ius' | 'cus' | 'ibus' | 'im' | 'none'
} => {
  // Priority orders as specified by business requirements
  const group1Priority: ('ios' | 'cos')[] = ['ios', 'cos']
  const group2Priority: ('ius' | 'cus' | 'ibus' | 'im')[] = [
    'ibus',
    'ius',
    'cus',
    'im',
  ]

  // Helper to check if metric has data (value or v2 source data)
  const hasData = (metricKey: string): boolean => {
    const z = zone as any
    return (
      z[`${metricKey}_min`] != null ||
      z[`${metricKey}_max`] != null ||
      z[`${metricKey}_min_value`] != null ||
      z[`${metricKey}_max_value`] != null ||
      z[`${metricKey}_value`] != null ||
      z[`${metricKey}_max_default_source`] !== undefined
    )
  }

  // Find first metric with data for group1, fallback to 'none'
  const group1Selection =
    zone?.indice_occupation ||
    group1Priority.find((metric) => hasData(metric)) ||
    'none'

  // Find first metric with data for group2, fallback to 'none'
  const group2Selection =
    zone?.indice_utilisation ||
    group2Priority.find((metric) => hasData(metric)) ||
    'none'

  return {
    group1: group1Selection?.toLowerCase() as 'ios' | 'cos' | 'none',
    group2: group2Selection?.toLowerCase() as
      | 'ius'
      | 'cus'
      | 'ibus'
      | 'im'
      | 'none',
  }
}

/**
 * Calculates derived surface from index and determining land area (STd)
 * @param std - Determining land area in m²
 * @param index - The index value (IOS, COS, IUS, CUS, IBUS, IM)
 * @returns Calculated surface in m² (rounded to nearest integer)
 */
export const calculateDerivedSurface = (std: number, index: number): number => {
  return Math.round(std * index)
}

/**
 * Formats min/max range for display
 * @param min - Minimum value
 * @param max - Maximum value
 * @param unit - Optional unit string
 * @returns Formatted string like "0.2 / 0.5" or "0.5" or "-"
 */
export const formatMinMaxRange = (
  min?: number | string | null,
  max?: number | string | null,
  unit?: string
): string => {
  // Convert to numbers if strings (API might return string values)
  const minNum =
    min != null ? (typeof min === 'string' ? parseFloat(min) : min) : null
  const maxNum =
    max != null ? (typeof max === 'string' ? parseFloat(max) : max) : null

  const minValid = minNum != null && !isNaN(minNum)
  const maxValid = maxNum != null && !isNaN(maxNum)

  if (minValid && maxValid) {
    const formatted = `${minNum.toFixed(2)} / ${maxNum.toFixed(2)}`
    return unit ? `${formatted} ${unit}` : formatted
  }
  if (maxValid) {
    const formatted = maxNum.toFixed(2)
    return unit ? `${formatted} ${unit}` : formatted
  }
  if (minValid) {
    const formatted = minNum.toFixed(2)
    return unit ? `${formatted} ${unit}` : formatted
  }
  return '-'
}

/**
 * Gets the color for a source type
 * @param source - The source type
 * @param isUserEdited - Whether the value is user-edited
 * @returns Color string (hex or theme variable)
 */
export const getSourceColor = (
  source?: string,
  isUserEdited?: boolean
): string => {
  if (isUserEdited) return 'inherit' // User-edited values use default color (badge indicates edit)
  if (source === 'estimate') return '#FFD60A' // Warning yellow for estimates
  return 'inherit' // Default black for manual/geoportal/llm
}

/**
 * Checks if a metric is in an exclusive group
 * @param metricKey - The metric key to check
 * @returns The group identifier if in an exclusive group, null otherwise
 */
export const getMetricExclusiveGroup = (
  metricKey: string
): 'group1' | 'group2' | null => {
  const groups = getExclusiveGroups()

  if (groups.group1.includes(metricKey)) return 'group1'
  if (groups.group2.includes(metricKey)) return 'group2'
  return null
}
